import { InputProps as ChakraInputProps, Input as ChakraInput, GroupProps, mergeRefs, IconButton, ButtonProps } from '@chakra-ui/react';
import * as React from 'react';
import { FieldValues, Path, PathValue, useController, useFormContext } from 'react-hook-form';
import { useWatchCallback } from './useWatchCallback';
import { Field, FieldProps, InputGroup, SvgIcon } from '../chakra-components';

export interface InputProps<T extends FieldValues, TPath extends Path<T>> extends ChakraInputProps {
	ref?: React.Ref<HTMLInputElement>,
	linkTo: TPath,
	onWatch?: (value: PathValue<T, TPath>) => void,
	disabled?: boolean,
	label?: React.ReactNode,
	startElement?: React.ReactNode,
	endElement?: React.ReactNode,
	groupProps?: GroupProps,
	fieldProps?: FieldProps,
	hideErrorText?: boolean,
	formatFieldValue?: (value: PathValue<T, TPath>) => string; // Formatea como queremos que aparezca el valor en el input
	unFormatFieldValue?: (value: string) => string; // Deshace el formator anterior y usa el equivalente al toString() del PathValue<T, TPath>
}


export const InputIconButton = ({ icon, ...props }: Omit<ButtonProps, 'children'> & { icon: SvgIcon }) => {
	return <IconButton
		bg="transparent"
		color={{ base: '{colors.neutral.800}', _dark: 'white' }}
		tabIndex={-1}
		marginX="-3"
		aspectRatio="square"
		variant="ghost"
		height="calc(100% - var(--chakra-space-2))"
		{...props}
	>
		<SvgIcon src={icon} h='6' w='6' />
	</IconButton>;
};

// De momento expongo como ref tal cual un HtmlInputElement, si queremos caparlo el día mañana podemos hacer lo que hacemos con el form
export type InputRef = HTMLInputElement;

export const Input = <T extends FieldValues, TPath extends Path<T>>(props: InputProps<T, TPath>) => {
	const {
		ref,
		linkTo,
		disabled,
		label,
		startElement,
		endElement,
		hideErrorText,
		groupProps,
		fieldProps,
		formatFieldValue,
		unFormatFieldValue,
		onWatch,
		...inputProps
	} = props;

	const { control } = useFormContext<T>();


	// Hook de react-hook-form para manejar el input
	const { field, fieldState } = useController({ name: linkTo, control, disabled });

	const invalid = !!fieldState.error;

	useWatchCallback(control, linkTo, onWatch);

	const onFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select();
		inputProps.onFocus?.(e);
	}, [inputProps]);

	const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {

		const value = e.currentTarget.value;
		const normalized = unFormatFieldValue ? unFormatFieldValue(value) : value;
		field.onChange(normalized);

	}, [field, unFormatFieldValue]);

	return (
		<Field ref={ref} invalid={invalid} label={inputProps.required ? <><>{label}</> <sup>*</sup></> : label} errorText={hideErrorText ? '' : fieldState.error?.message} {...fieldProps}>
			<InputGroup width='full' overflow={'hidden'} startElement={startElement} endElement={endElement} {...groupProps}>
				<ChakraInput
					{...field}
					value={formatFieldValue ? formatFieldValue(field.value) : field.value}
					ref={mergeRefs(ref, field.ref)}
					{...inputProps}
					onFocus={onFocus}
					onChange={onChange}
					bg={{ base: 'white', _dark: '{colors.neutral.800}' }}
				/>
			</InputGroup>
		</Field>
	);

};

